import { useState } from "react";
import { Send, MapPin, Phone, Mail, Instagram, Linkedin, MessageCircle, Globe } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageSEO } from "@/components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SPACE_TYPES = [
  "Fitness & Active Spaces",
  "Spa & Wellness",
  "Salons & Beauty",
  "Hotel & Hospitality",
  "Restaurants & Fine Dining",
  "Corporate & Office",
  "Retail & Boutiques",
  "Residential & Homes",
  "Lounges & Member Clubs",
  "Other"
];

const PRODUCT_INTERESTS = [
  "Fragrance Oils Only",
  "Diffuser Machines Only",
  "Both Oils & Machines",
  "Custom Scent Development",
  "Consultation"
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    space_type: "",
    product_interest: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/inquiry`, formData);
      setSubmitted(true);
      toast.success("Thank you for your inquiry. We'll be in touch soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        space_type: "",
        product_interest: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageSEO page="contact" />
      
      {/* Hero Section */}
      <section className="section-luxury pt-32 pb-16" data-testid="contact-hero">
        <div className="container-luxury">
          <div className="max-w-4xl">
            <p className="text-overline mb-6">Get in Touch</p>
            <h1 className="text-display mb-8">
              Begin Your Olfactory<br />Journey With Us
            </h1>
            <div className="divider-luxury" />
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="pb-24" data-testid="contact-content">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
            {/* Contact Form */}
            <div className="lg:col-span-7">
              {submitted ? (
                <div 
                  className="py-16 text-center"
                  style={{ backgroundColor: 'var(--surface)' }}
                  data-testid="success-message"
                >
                  <h2 className="font-heading text-3xl font-light mb-4">
                    Thank You
                  </h2>
                  <p className="text-subtitle mb-8">
                    We've received your inquiry and will be in touch within 24 hours.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="btn-luxury-outline"
                    data-testid="send-another-btn"
                  >
                    Send Another Inquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="inquiry-form">
                  <h2 className="sr-only">Enquiry Form</h2>
                  <div className="space-y-8">
                    {/* Name & Email Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <label 
                          htmlFor="name" 
                          className="text-overline block mb-3"
                        >
                          Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          className="form-input-luxury"
                          placeholder="Your full name"
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <label 
                          htmlFor="email" 
                          className="text-overline block mb-3"
                        >
                          Email *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className="form-input-luxury"
                          placeholder="your@email.com"
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    {/* Phone & Company Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <label 
                          htmlFor="phone" 
                          className="text-overline block mb-3"
                        >
                          Phone
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="form-input-luxury"
                          placeholder="+91 85274 99057"
                          data-testid="input-phone"
                        />
                      </div>
                      <div>
                        <label 
                          htmlFor="company" 
                          className="text-overline block mb-3"
                        >
                          Company / Brand
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          className="form-input-luxury"
                          placeholder="Your organization"
                          data-testid="input-company"
                        />
                      </div>
                    </div>

                    {/* Space Type & Product Interest Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <label className="text-overline block mb-3">
                          Space Type
                        </label>
                        <Select 
                          value={formData.space_type}
                          onValueChange={(value) => handleSelectChange("space_type", value)}
                        >
                          <SelectTrigger 
                            className="w-full bg-transparent border-0 border-b rounded-none focus:ring-0 h-auto py-4"
                            style={{ borderColor: 'var(--accent)' }}
                            data-testid="select-space-type"
                          >
                            <SelectValue placeholder="Select space type" />
                          </SelectTrigger>
                          <SelectContent 
                            className="border rounded-none"
                            style={{ 
                              backgroundColor: 'var(--background)',
                              borderColor: 'var(--border)'
                            }}
                          >
                            {SPACE_TYPES.map((type) => (
                              <SelectItem 
                                key={type} 
                                value={type}
                                className="cursor-pointer"
                              >
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-overline block mb-3">
                          Interest
                        </label>
                        <Select 
                          value={formData.product_interest}
                          onValueChange={(value) => handleSelectChange("product_interest", value)}
                        >
                          <SelectTrigger 
                            className="w-full bg-transparent border-0 border-b rounded-none focus:ring-0 h-auto py-4"
                            style={{ borderColor: 'var(--accent)' }}
                            data-testid="select-product-interest"
                          >
                            <SelectValue placeholder="What interests you?" />
                          </SelectTrigger>
                          <SelectContent 
                            className="border rounded-none"
                            style={{ 
                              backgroundColor: 'var(--background)',
                              borderColor: 'var(--border)'
                            }}
                          >
                            {PRODUCT_INTERESTS.map((interest) => (
                              <SelectItem 
                                key={interest} 
                                value={interest}
                                className="cursor-pointer"
                              >
                                {interest}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label 
                        htmlFor="message" 
                        className="text-overline block mb-3"
                      >
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={handleInputChange}
                        className="form-input-luxury resize-none"
                        placeholder="Tell us about your space and what you're looking for..."
                        data-testid="input-message"
                      />
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="btn-luxury inline-flex items-center gap-2 disabled:opacity-50 transition-opacity"
                        data-testid="submit-btn"
                      >
                        {submitting ? (
                          "Sending..."
                        ) : (
                          <>
                            Send Inquiry
                            <Send className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>

            {/* Contact Information */}
            <div className="lg:col-span-5">
              <div 
                className="p-8 lg:p-12"
                style={{ backgroundColor: 'var(--surface)' }}
                data-testid="contact-info"
              >
                <h2 className="font-heading text-2xl font-light mb-8">
                  Contact Information
                </h2>

                <div className="space-y-8">
                  <div className="flex items-start gap-4">
                    <Mail className="w-5 h-5 mt-1" style={{ color: 'var(--secondary-text)' }} />
                    <div>
                      <h3 className="text-overline mb-2">Email</h3>
                      <a 
                        href="mailto:hello@ikarascents.com"
                        className="text-base transition-opacity hover:opacity-70"
                        data-testid="contact-email"
                      >
                        hello@ikarascents.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Phone className="w-5 h-5 mt-1" style={{ color: 'var(--secondary-text)' }} />
                    <div>
                      <h3 className="text-overline mb-2">Phone</h3>
                      <a 
                        href="tel:+918527499057"
                        className="text-base transition-opacity hover:opacity-70"
                        data-testid="contact-phone"
                      >
                        +91 85274 99057
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Globe className="w-5 h-5 mt-1" style={{ color: 'var(--secondary-text)' }} />
                    <div>
                      <h3 className="text-overline mb-2">Website</h3>
                      <a 
                        href="https://www.ikarascents.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-base transition-opacity hover:opacity-70"
                        data-testid="contact-website"
                      >
                        www.ikarascents.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 mt-1" style={{ color: 'var(--secondary-text)' }} />
                    <div>
                      <h3 className="text-overline mb-2">Delhi Office</h3>
                      <address 
                        className="not-italic text-base leading-relaxed"
                        style={{ color: 'var(--secondary-text)' }}
                        data-testid="contact-address-delhi"
                      >
                        B-72, Ashoka Enclave II,<br />
                        Sector 37, Faridabad, 121003
                      </address>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 mt-1" style={{ color: 'var(--secondary-text)' }} />
                    <div>
                      <h3 className="text-overline mb-2">Indore Office</h3>
                      <address 
                        className="not-italic text-base leading-relaxed"
                        style={{ color: 'var(--secondary-text)' }}
                        data-testid="contact-address-indore"
                      >
                        Platinum Plaza, PU 4,<br />
                        Behind C21 Mall, AB Road, 452010
                      </address>
                    </div>
                  </div>
                </div>

                <div 
                  className="mt-12 pt-8 border-t"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <h3 className="text-overline mb-4">Business Hours</h3>
                  <p className="text-sm" style={{ color: 'var(--secondary-text)' }}>
                    Monday – Friday<br />
                    10:00 AM – 6:00 PM IST
                  </p>
                </div>

                <div 
                  className="mt-8 pt-8 border-t"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <h3 className="text-overline mb-4">Follow Us</h3>
                  <div className="flex gap-4">
                    <a 
                      href="https://instagram.com/ikarascents" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 transition-opacity hover:opacity-70"
                      style={{ color: 'var(--primary-text)' }}
                      data-testid="social-instagram"
                      aria-label="Follow Ikara Scents on Instagram"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a 
                      href="#" 
                      className="p-2 transition-opacity hover:opacity-70"
                      style={{ color: 'var(--primary-text)' }}
                      data-testid="social-linkedin"
                      aria-label="Follow Ikara Scents on LinkedIn"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                    <a 
                      href="#" 
                      className="p-2 transition-opacity hover:opacity-70"
                      style={{ color: 'var(--primary-text)' }}
                      data-testid="social-whatsapp"
                      aria-label="Contact Ikara Scents on WhatsApp"
                    >
                      <MessageCircle className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
